import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { codexApi } from '../api';
import { celebrate, bigCelebrate } from '../../lib/celebrate';
import AudioButton from '../components/AudioButton';
import { ChevronRight, Check, X, Sparkles, BookOpen, Lightbulb, Eye, Feather } from 'lucide-react';

const LAYER_META = {
  1: { icon: BookOpen,  title: 'Literal',   subtitle: 'What the word actually says' },
  2: { icon: Lightbulb, title: 'Cultural',  subtitle: 'How the ancients heard it' },
  3: { icon: Eye,       title: 'Prophetic', subtitle: 'How it reveals Christ' },
};

export default function CodexDailyScroll({ refresh }) {
  const navigate = useNavigate();
  const [scroll, setScroll] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeLayer, setActiveLayer] = useState(1);
  const [selected, setSelected] = useState(null);
  const [feedback, setFeedback] = useState(null); // {correct, explanation, ...}
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const s = await codexApi.dailyScroll();
      setScroll(s);
      const next = Math.min(s.layers_completed + 1, 3);
      setActiveLayer(next);
      setSelected(null);
      setFeedback(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  if (loading) return <div style={{ padding: 40, textAlign: 'center', opacity: 0.7 }}>Unfurling the scroll…</div>;
  if (error)   return <div style={{ padding: 40, textAlign: 'center', color: 'salmon' }}>{error}</div>;
  if (!scroll) return null;

  const layerKey = `layer${activeLayer}`;
  const layer = scroll[layerKey];
  const completed = scroll.layers_completed >= activeLayer;
  const allDone = scroll.layers_completed >= 3 || scroll.today_completed;

  const submit = async () => {
    if (selected == null || submitting) return;
    setSubmitting(true);
    try {
      const res = await codexApi.submitAnswer(scroll.word_id, activeLayer, selected);
      setFeedback(res);
      if (res.correct) {
        if (res.word_complete) bigCelebrate();
        else celebrate(0.55);
      }
      // Refresh global state for streak/level updates in nav
      refresh();
    } catch (e) {
      alert(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const goNextLayer = () => {
    setSelected(null);
    setFeedback(null);
    setActiveLayer((l) => Math.min(l + 1, 3));
  };

  return (
    <div data-testid="codex-daily-scroll" style={{ paddingTop: 8 }}>
      {/* Word title */}
      <motion.div
        key={scroll.word_id}
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        className="codex-card-gold codex-card"
        style={{ padding: 28, marginBottom: 18, textAlign: 'center', position: 'relative' }}
      >
        <div className="codex-eyebrow">{scroll.language === 'hebrew' ? 'HEBREW SCROLL' : 'GREEK SCROLL'} {scroll.strongs ? `· ${scroll.strongs}` : ''}</div>
        <motion.div
          initial={{ scale: 0.7, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 0.15, type: 'spring', damping: 14 }}
          className={scroll.language === 'hebrew' ? 'codex-hebrew' : 'codex-greek'}
          style={{ fontSize: 'clamp(48px, 8vw, 88px)', color: 'var(--codex-gold-light)', lineHeight: 1, margin: '12px 0' }}
        >
          {scroll.script}
        </motion.div>
        <div className="codex-display" style={{ fontSize: 28, color: 'var(--codex-parchment)' }}>{scroll.transliteration}</div>
        <div style={{ opacity: 0.75, fontSize: 13, marginTop: 4 }}>{scroll.short_meaning}</div>
        <div style={{ marginTop: 14, display: 'flex', justifyContent: 'center', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          <AudioButton wordId={scroll.word_id} label="Hear it" />
          <span style={{ fontSize: 12, opacity: 0.55 }}>{scroll.pronunciation_hint}</span>
        </div>
      </motion.div>

      {/* Layer progress dots */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 14, margin: '14px 0 24px', flexWrap: 'wrap' }}>
        {[1, 2, 3].map((n) => {
          const meta = LAYER_META[n];
          const Icon = meta.icon;
          const isDone = scroll.layers_completed >= n;
          const isActive = activeLayer === n;
          return (
            <button
              key={n}
              data-testid={`codex-layer-tab-${n}`}
              disabled={n > scroll.layers_completed + 1}
              onClick={() => { setActiveLayer(n); setSelected(null); setFeedback(null); }}
              className="codex-card"
              style={{
                padding: '10px 14px',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                cursor: n > scroll.layers_completed + 1 ? 'not-allowed' : 'pointer',
                borderColor: isActive ? 'rgba(212,175,55,0.7)' : undefined,
                background: isActive ? 'linear-gradient(90deg, rgba(212,175,55,0.18), rgba(168,85,247,0.1))' : undefined,
                opacity: n > scroll.layers_completed + 1 ? 0.45 : 1,
                color: 'var(--codex-parchment)',
              }}
            >
              <span style={{
                width: 22, height: 22, borderRadius: '50%',
                background: isDone ? 'var(--codex-gold)' : 'transparent',
                border: '1px solid var(--codex-gold)',
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                color: isDone ? 'var(--codex-ink)' : 'var(--codex-gold-light)',
                fontSize: 11, fontWeight: 700,
              }}>
                {isDone ? <Check size={12} /> : n}
              </span>
              <Icon size={14} />
              <span className="codex-display" style={{ fontSize: 13 }}>{meta.title}</span>
            </button>
          );
        })}
      </div>

      {/* Layer content */}
      <AnimatePresence mode="wait">
        <motion.div
          key={`${activeLayer}-${feedback ? 'fb' : 'q'}`}
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -16 }}
          transition={{ duration: 0.25 }}
          className="codex-card"
          style={{ padding: 26 }}
          data-testid={`codex-layer-content-${activeLayer}`}
        >
          {!feedback && !completed && (
            <>
              <div className="codex-eyebrow" style={{ marginBottom: 8 }}>LAYER {activeLayer} · {LAYER_META[activeLayer].subtitle}</div>
              <h2 className="codex-h2" style={{ color: 'var(--codex-gold-light)', marginBottom: 18 }}>{layer.prompt}</h2>
              <div style={{ display: 'grid', gap: 10 }}>
                {layer.options.map((opt, i) => {
                  const isSel = selected === i;
                  return (
                    <motion.button
                      key={i}
                      whileHover={{ x: 4 }}
                      onClick={() => setSelected(i)}
                      data-testid={`codex-option-${i}`}
                      className="codex-card"
                      style={{
                        padding: 14,
                        textAlign: 'left',
                        cursor: 'pointer',
                        borderColor: isSel ? 'rgba(212,175,55,0.7)' : undefined,
                        background: isSel ? 'linear-gradient(90deg, rgba(212,175,55,0.18), rgba(168,85,247,0.1))' : undefined,
                        color: 'var(--codex-parchment)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 12,
                      }}
                    >
                      <span style={{
                        width: 24, height: 24, borderRadius: 6,
                        border: '1px solid var(--codex-gold)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                        color: isSel ? 'var(--codex-ink)' : 'var(--codex-gold-light)',
                        background: isSel ? 'var(--codex-gold)' : 'transparent',
                        fontSize: 11, fontWeight: 700,
                      }}>{String.fromCharCode(65 + i)}</span>
                      <span style={{ fontSize: 14 }}>{opt}</span>
                    </motion.button>
                  );
                })}
              </div>
              <div style={{ marginTop: 20, display: 'flex', justifyContent: 'flex-end' }}>
                <button
                  disabled={selected == null || submitting}
                  onClick={submit}
                  className="codex-btn codex-btn-primary"
                  style={{ opacity: selected != null && !submitting ? 1 : 0.4 }}
                  data-testid="codex-submit-answer"
                >
                  {submitting ? 'Sealing…' : 'Submit'} <ChevronRight size={16} />
                </button>
              </div>
            </>
          )}

          {feedback && (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                {feedback.correct ? (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, color: '#7ee2a3' }}>
                    <Check size={20} /> <strong>Correct</strong>
                  </span>
                ) : (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, color: '#f87171' }}>
                    <X size={20} /> <strong>Not quite</strong>
                  </span>
                )}
                {feedback.xp_gained > 0 && (
                  <span style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 6, color: 'var(--codex-gold-light)' }}>
                    <Sparkles size={14} /> +{feedback.xp_gained} XP
                  </span>
                )}
              </div>
              <p style={{ lineHeight: 1.7, opacity: 0.92 }}>{feedback.explanation}</p>

              {feedback.streak_saved && (
                <div className="codex-card" style={{ padding: 14, marginTop: 12, borderRadius: 12, background: 'rgba(125,211,252,0.12)', borderColor: 'rgba(125,211,252,0.4)' }}>
                  <strong style={{ color: '#7dd3fc' }}>❄️ Streak Shield used.</strong>{' '}
                  Your streak survived a missed day. You have {feedback.streak_freezes} shield{feedback.streak_freezes === 1 ? '' : 's'} left.
                </div>
              )}
              {feedback.freeze_earned && (
                <div className="codex-card" style={{ padding: 14, marginTop: 12, borderRadius: 12, background: 'rgba(125,211,252,0.12)', borderColor: 'rgba(125,211,252,0.4)' }}>
                  <strong style={{ color: '#7dd3fc' }}>❄️ New Streak Shield earned!</strong>{' '}
                  You've reached a 7-day milestone — you now have {feedback.streak_freezes} shield{feedback.streak_freezes === 1 ? '' : 's'} protecting your streak.
                </div>
              )}

              {feedback.badges_unlocked && feedback.badges_unlocked.length > 0 && (
                <div className="codex-card-gold" style={{ padding: 14, marginTop: 16, borderRadius: 12 }}>
                  <strong className="codex-display" style={{ color: 'var(--codex-gold)' }}>🎖 Badge unlocked:</strong>{' '}
                  {feedback.badges_unlocked.join(', ')}
                </div>
              )}

              <div style={{ marginTop: 22, display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
                <button
                  onClick={() => { setFeedback(null); setSelected(null); load(); }}
                  className="codex-btn codex-btn-ghost"
                >
                  Refresh
                </button>
                {feedback.word_complete ? (
                  <button onClick={() => window.history.back()} className="codex-btn codex-btn-primary" data-testid="codex-scroll-complete">
                    <Sparkles size={16} /> Scroll Sealed — Walk On
                  </button>
                ) : feedback.correct ? (
                  <button onClick={goNextLayer} className="codex-btn codex-btn-primary" data-testid="codex-next-layer">
                    Next Layer <ChevronRight size={16} />
                  </button>
                ) : (
                  <button onClick={() => { setFeedback(null); setSelected(null); }} className="codex-btn codex-btn-primary">
                    Try Again
                  </button>
                )}
              </div>
            </>
          )}

          {completed && !feedback && (
            <div style={{ textAlign: 'center', padding: '12px 0' }}>
              <Check size={36} color="#7ee2a3" />
              <div className="codex-display" style={{ fontSize: 22, marginTop: 12 }}>This layer is sealed</div>
              <div style={{ opacity: 0.75, marginTop: 6, fontSize: 14 }}>
                You have already decoded this layer today.
              </div>
              {activeLayer < 3 && (
                <button onClick={goNextLayer} className="codex-btn codex-btn-primary" style={{ marginTop: 18 }}>
                  Open Next Layer <ChevronRight size={16} />
                </button>
              )}
            </div>
          )}
        </motion.div>
      </AnimatePresence>

      {/* Reflection prompt at the end */}
      {allDone && (
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          className="codex-card-gold codex-card"
          style={{ padding: 22, marginTop: 18 }}
        >
          <div className="codex-eyebrow">SACRED REFLECTION</div>
          <p style={{ marginTop: 8, lineHeight: 1.7, fontSize: 15 }}>{scroll.reflection_prompt}</p>
          <button
            onClick={() => navigate('/codex/journal', {
              state: {
                prompt: scroll.reflection_prompt,
                word_id: scroll.word_id,
                word_transliteration: scroll.transliteration,
              },
            })}
            className="codex-btn codex-btn-primary"
            style={{ marginTop: 14 }}
            data-testid="codex-reflect-cta"
          >
            <Feather size={16} /> Reflect in My Journal
          </button>
        </motion.div>
      )}
    </div>
  );
}
